import { HStack, Stack, Text, Textarea } from "@chakra-ui/react";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import { MinimumRequirementResponseType, type ViewDocumentAsOwnerDto } from "../../../autogen/bff-api";
import { verdictToStatusVariant } from "../../../common/components/StatusVariant";
import { DownloadDocumentIconButton } from "../../../common/documents/DownloadDocumentIconButton/DownloadDocumentIconButton";
import { ReadonlyAnswerCard, ReadonlyFieldLabel } from "../../sourcing-events/bid-readonly/ReadonlyAnswerCard";
import { QualificationVerdictControl } from "./QualificationVerdictControl";

interface Props {
  id: string;
  title: string;
  description?: string | null;
  responseType: MinimumRequirementResponseType;
  supplierAnswer: boolean | undefined;
  supplierComment?: string | null;
  supplierAttachments?: ViewDocumentAsOwnerDto[] | null;
  attachmentsHidden?: boolean;
  verdict: boolean | null;
  comment: string;
  onVerdictChange: (id: string, value: boolean | null) => void;
  onCommentChange: (id: string, value: string) => void;
  isReadOnly?: boolean;
}

// Memoized so editing one card does not re-render (and re-render the read-only TipTap description
// of) every other card. The parent passes stable per-id handlers, so only props of the edited row
// change.
export const MinimumRequirementReviewCard = memo(function MinimumRequirementReviewCard({
  id,
  title,
  description,
  responseType,
  supplierAnswer,
  supplierComment,
  supplierAttachments,
  attachmentsHidden = false,
  verdict,
  comment,
  onVerdictChange,
  onCommentChange,
  isReadOnly,
}: Props) {
  const { t } = useTranslation();
  const asksForDocument = responseType === MinimumRequirementResponseType.YesNoDocument;
  // A requirement the supplier never answered (e.g. added after this bid was submitted) cannot be
  // reviewed — the backend rejects a verdict for it — so lock the controls instead of letting the
  // buyer make an edit that would be discarded.
  const reviewable = supplierAnswer !== undefined;
  const commentLocked = isReadOnly || !reviewable;

  return (
    <ReadonlyAnswerCard
      accent={verdictToStatusVariant(verdict)}
      title={title}
      descriptionHtml={description}
      answer={
        supplierAnswer === undefined
          ? { kind: "empty" }
          : { kind: "boolean", value: supplierAnswer, comment: supplierComment }
      }
      headerAction={
        reviewable ? (
          <QualificationVerdictControl value={verdict} onChange={(v) => onVerdictChange(id, v)} isReadOnly={isReadOnly} />
        ) : (
          <Stack spacing="1" align="flex-end">
            <QualificationVerdictControl value={verdict} isLocked />
            <Text fontSize="xs" color="smMuted" textAlign="right">
              {t("The supplier has not answered this requirement")}
            </Text>
          </Stack>
        )
      }
      footer={
        <Stack spacing="2">
          {asksForDocument && (
            <Stack spacing="2" mb="2">
              <ReadonlyFieldLabel>{t("Documentation")}</ReadonlyFieldLabel>
              {attachmentsHidden ? (
                <Text fontSize="sm" color="smMuted">
                  {t("Hidden until the jury's decision is registered")}
                </Text>
              ) : supplierAttachments?.length ? (
                supplierAttachments.map((document) => (
                  <HStack
                    key={document.id}
                    spacing="2"
                    justify="space-between"
                    border="1px solid"
                    borderColor="smBorder"
                    rounded="md"
                    px="3"
                    py="2"
                  >
                    <Text fontSize="sm" isTruncated>
                      {document.name}
                    </Text>
                    <DownloadDocumentIconButton documentId={document.id} size="sm" />
                  </HStack>
                ))
              ) : (
                <Text fontSize="sm" color="smMuted">
                  {t("No documentation attached")}
                </Text>
              )}
            </Stack>
          )}
          <ReadonlyFieldLabel>{t("Evaluation comment")}</ReadonlyFieldLabel>
          {commentLocked && comment ? (
            <Text fontSize="sm" color="smText" whiteSpace="pre-wrap" maxW="82ch">
              {comment}
            </Text>
          ) : (
            <Textarea
              value={comment}
              onChange={(e) => onCommentChange(id, e.target.value)}
              placeholder={commentLocked ? undefined : String(t("Write comment..."))}
              rows={3}
              isDisabled={commentLocked}
              resize="vertical"
              bg="smBackground"
            />
          )}
        </Stack>
      }
    />
  );
});
